import {
  keyframes,
  FormControlLabel,
  formControlLabelClasses,
  Stack,
  type StackProps,
  Tooltip,
  tooltipClasses,
} from '@mui/material';

import createSvgIcon from 'shared/utils/createSvgIcon';
import styled from 'shared/utils/styled';

const ChatIcon = createSvgIcon(
  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />,
  '0 0 24 24'
);

export const ChatContainer = styled('div')(() => ({
  background: 'rgb(242, 244, 248)',
}));

export const ChatFormContainer = (props: StackProps) => (
  <Stack
    spacing={2}
    sx={{ padding: { xs: '15px 0px', sm: '15px' } }}
    {...props}
  />
);

export const ChatTextbox = styled('textarea')(() => ({
  display: 'block',
  width: '100%',
  minHeight: 80,
  padding: 15,

  fontFamily: 'NeoSansStd',
  fontSize: '0.875rem',

  border: '1px solid rgba(0,0,0,.2)',
  borderRadius: 2,
  outline: 'none',

  resize: 'none',
}));

export const MessageAuthor = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 600,
  color: '#347AC4',
}));

export const MessageCreationDate = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  lineHeight: '20px',
  color: 'rgba(100, 100, 100, .6)',
}));

type MessageProps = {
  isDeleted?: boolean;
  isRead: boolean;
};

export const Message = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isRead' && prop !== 'isDeleted',
})<MessageProps>(({ theme, isDeleted, isRead }) => ({
  display: 'block',
  padding: '15px 20px',

  [theme.breakpoints.down('sm')]: {
    padding: '10px 8px',
  },

  fontSize: theme.typography.pxToRem(14),

  background: '#fff',
  borderRight: '2px solid transparent',

  wordBreak: 'break-word',

  ...(!isRead && {
    borderRightColor: theme.palette.success.main,
  }),

  ...(isDeleted && {
    fontSize: '0.8rem',
    fontStyle: 'italic',
    fontWeight: 300,
    textAlign: 'right',
  }),
}));

export const Edited = styled('span')(() => ({
  display: 'block',

  fontSize: '0.75rem',
  fontStyle: 'italic',
  fontWeight: 300,
  color: 'rgb(100, 100, 100)',
  textAlign: 'right',
}));

export const EditRibbon = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  padding: '5px 10px',

  fontSize: '0.8125rem',

  background: '#fff',
}));

export const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  [`& .${formControlLabelClasses.label}`]: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),

    fontFamily: 'NeoSansStd',
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1,
  },
}));

const pulsate = keyframes`
  from {
    transform: scale(3.5)
  }
  to {
    transform: scale(6)
  }
`;

const StyledChatIcon = styled(ChatIcon)(() => ({
  fontSize: '1.5rem',
  transform: 'scale(3.5)',
  animation: `${pulsate} 2s linear infinite alternate`,
}));

export const StyledTooltip = styled(Tooltip)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 14,
    fontFamily: 'NeoSansStd',
  },
}));

export const ChatLoader = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    width="100%"
    minHeight="180px"
  >
    <StyledChatIcon />
  </Stack>
);
